import { createServerFn } from "@tanstack/react-start";
import { sendLovableEmail } from "@lovable.dev/email-js";
import { z } from "zod";

const InputSchema = z.object({
  email: z.string().email().max(255),
  locale: z.enum(["de", "en"]),
  // Note: toolUsageId is intentionally NOT accepted from the client.
  // Trusting a client-supplied sequential id would allow tampering with
  // arbitrary rows in tool_usage. We always insert a fresh row instead.
  payload: z.object({
    aov: z.number(),
    grossMargin: z.number(),
    marketingMargin: z.number(),
    conversionRate: z.number(),
    industrySlug: z.string().max(64),
    industryLabel: z.string().max(128),
    maxCPA: z.number(),
    maxCPC: z.number(),
    industryMedianCPC: z.number(),
    verdict: z.enum(["solid", "tight", "problematic"]),
    cpcGapToMedianPct: z.number(),
  }),
});


const REPORT_TO = "andre.freier@calvarius.com";
const FROM = "Calvarius Rechner <rechner@notify.calvarius.com>";
const SENDER_DOMAIN = "notify.calvarius.com";

export const requestViabilityReport = createServerFn({ method: "POST" })
  .inputValidator((input) => InputSchema.parse(input))
  .handler(async ({ data }) => {
    const supabaseUrl = process.env.SUPABASE_URL;
    const serviceKey = process.env.SUPABASE_SERVICE_ROLE_KEY;
    if (!supabaseUrl || !serviceKey) throw new Error("missing_env");

    const { createClient } = await import("@supabase/supabase-js");
    const supabase = createClient(supabaseUrl, serviceKey);

    // Always insert a new row — never trust a client-supplied id.
    await supabase.from("tool_usage").insert({
      tool_type: "viability_calculator",
      email_report_requested: true,
      email_for_report: data.email,
      viability_data: {
        ...data.payload,
        locale: data.locale,
        email_report_requested: true,
      },
    });


    const p = data.payload;
    const isDe = data.locale === "de";
    const subject = isDe
      ? `Wirtschaftlichkeits-Report angefordert — ${p.verdict.toUpperCase()} · ${p.industryLabel}`
      : `Viability report requested — ${p.verdict.toUpperCase()} · ${p.industryLabel}`;

    const html = `<!doctype html><html><body style="font-family:system-ui,sans-serif;max-width:680px;margin:0 auto;padding:24px;color:#111;">
<h1 style="font-size:20px;margin:0 0 16px;">Wirtschaftlichkeits-Report angefordert</h1>
<p style="font-size:14px;color:#555;margin:0 0 20px;">Sprache: ${data.locale.toUpperCase()}</p>
<table style="width:100%;border-collapse:collapse;font-size:14px;">
  <tr><td style="padding:6px 0;color:#666;width:200px;">E-Mail</td><td style="padding:6px 0;"><strong>${escapeHtml(data.email)}</strong></td></tr>
  <tr><td style="padding:6px 0;color:#666;">Branche</td><td style="padding:6px 0;">${escapeHtml(p.industryLabel)}</td></tr>
  <tr><td style="padding:6px 0;color:#666;">AOV</td><td style="padding:6px 0;">${p.aov.toFixed(2)} €</td></tr>
  <tr><td style="padding:6px 0;color:#666;">Bruttomarge</td><td style="padding:6px 0;">${p.grossMargin}%</td></tr>
  <tr><td style="padding:6px 0;color:#666;">Marketing-Marge</td><td style="padding:6px 0;">${p.marketingMargin}%</td></tr>
  <tr><td style="padding:6px 0;color:#666;">Conversion-Rate</td><td style="padding:6px 0;">${p.conversionRate}%</td></tr>
  <tr><td style="padding:6px 0;color:#666;">Max CPA</td><td style="padding:6px 0;"><strong>${p.maxCPA.toFixed(2)} €</strong></td></tr>
  <tr><td style="padding:6px 0;color:#666;">Max CPC</td><td style="padding:6px 0;"><strong>${p.maxCPC.toFixed(2)} €</strong></td></tr>
  <tr><td style="padding:6px 0;color:#666;">Branchen-Median</td><td style="padding:6px 0;">${p.industryMedianCPC.toFixed(2)} €</td></tr>
  <tr><td style="padding:6px 0;color:#666;">Gap zum Median</td><td style="padding:6px 0;">${p.cpcGapToMedianPct >= 0 ? "+" : ""}${p.cpcGapToMedianPct.toFixed(1)}%</td></tr>
  <tr><td style="padding:6px 0;color:#666;">Verdict</td><td style="padding:6px 0;"><strong>${p.verdict.toUpperCase()}</strong></td></tr>
</table>
<p style="margin-top:24px;font-size:13px;color:#555;">Bitte schriftliche Einordnung mit drei methodischen Empfehlungen an oben genannte E-Mail senden.</p>
</body></html>`;

    const text = `Wirtschaftlichkeits-Report angefordert
E-Mail: ${data.email}
Sprache: ${data.locale}
Branche: ${p.industryLabel}
AOV: ${p.aov} € | Bruttomarge: ${p.grossMargin}% | Marketing-Marge: ${p.marketingMargin}% | CVR: ${p.conversionRate}%
Max CPA: ${p.maxCPA} € | Max CPC: ${p.maxCPC} € | Median: ${p.industryMedianCPC} € | Gap: ${p.cpcGapToMedianPct}%
Verdict: ${p.verdict.toUpperCase()}`;

    const messageId = `viability-report-${Date.now()}-${data.email}`;

    const apiKey = process.env.LOVABLE_API_KEY;
    if (apiKey) {
      try {
        await sendLovableEmail(
          {
            to: REPORT_TO,
            from: FROM,
            sender_domain: SENDER_DOMAIN,
            subject,
            html,
            text,
            purpose: "transactional",
            label: "viability_report_request",
            idempotency_key: messageId,
            message_id: messageId,
            unsubscribe_token: crypto.randomUUID(),
          },
          { apiKey },
        );

        await supabase.from("email_send_log").insert({
          message_id: messageId,
          template_name: "viability_report_request",
          recipient_email: REPORT_TO,
          status: "sent",
        });
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err);
        console.error("sendLovableEmail failed", errMsg);
        await supabase.from("email_send_log").insert({
          message_id: messageId,
          template_name: "viability_report_request",
          recipient_email: REPORT_TO,
          status: "failed",
          error_message: errMsg,
        });
        // Don't throw — user-facing flow already succeeded (row updated)
      }
    } else {
      console.error("LOVABLE_API_KEY missing — viability report email not sent");
    }

    return { ok: true };
  });

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
